import PageSeo from '../seo/PageSeo';
import { useInitialData } from '../seo/InitialData';
import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import HomepageBlog from '../components/HomepageBlog'
import HomepagePhoto from '../components/HomepagePhoto'
import Pagination from '../components/Pagination';
import { readCatalogQuery } from '../hooks/useCatalogQuery';
import { getRandomCollectionPath } from './homepageUtils';
import { pageOf, sortBy, useArchive } from '../api/archive';

const homepageLogoSrc = `${process.env.PUBLIC_URL || ''}/assets/starekomputery-logo.png`;

export default function Homepage() {
  const initial = useInitialData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const { archive, error: archiveError } = useArchive();
  const page = readCatalogQuery(searchParams).page;
  const pageSize = 5;
  const allBlogs = useMemo(() => archive?.blogs || initial.blogs || [], [archive, initial.blogs]);
  const allCollections = useMemo(() => archive?.collections || initial.collections || [], [archive, initial.collections]);
  const blogPage = useMemo(() => pageOf(sortBy(allBlogs, 'Date', 'DESC'), page, pageSize), [allBlogs, page, pageSize]);
  const data = blogPage.entries;
  const totalPages = blogPage.totalPages;
  const dataNewPhoto = useMemo(() => sortBy(allCollections, 'createdAt', 'DESC').slice(0, 5), [allCollections]);
  const collectionTotal = archive?.collections.length ?? initial.collectionTotal ?? null;
  const blogTotal = archive?.blogs.length ?? initial.blogTotal ?? null;
  const blogError = Boolean(archiveError && !initial.blogs);
  const collectionError = Boolean(archiveError && !initial.collections);
  const collectionsLoading = !archive && !initial.collections;
  const blogsLoading = !archive && !initial.blogs;
  
  const randomCollectionPath = useMemo(() => getRandomCollectionPath(dataNewPhoto), [dataNewPhoto]);

  return (
    <section className="retro-homepage">
      <PageSeo />
      <div className="retro-homepage-inner">
        <div className="retro-hero">
          <div className="retro-hero-copy">
            <span className="retro-eyebrow">Kolekcja pasjonata / cyfrowe archiwum</span>
            <h1>Stare komputery.<br /><span>Wciąż pełne historii.</span></h1>
            <p>
              Komputery, konsole i oprogramowanie z epoki dyskietek.
              Odkrywaj zdjęcia, parametry i historie sprzętu — od Meritum po Amigę.
            </p>

            <form className="retro-home-search" role="search" action="/collection" onSubmit={(event) => {
              event.preventDefault();
              navigate(query.trim() ? `/collection?q=${encodeURIComponent(query.trim())}` : '/collection');
            }}>
              <i className="feather-search" aria-hidden="true"></i>
              <input name="q" type="search" maxLength={160} value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Szukaj sprzętu w kolekcji" placeholder="Np. Amiga 500, Atari, Z80…" />
              <button type="submit">Szukaj</button>
            </form>

            <div className="retro-discovery" aria-label="Pomysły na początek">
              <span>Zacznij od:</span>
              {['Amiga', 'Atari', 'Commodore', 'Oprogramowanie'].map((name) => <Link key={name} to={`/collection?q=${encodeURIComponent(name)}`}>{name}</Link>)}
            </div>

            <div className="retro-hero-actions">
              <Link className="retro-button retro-button-primary" to="/collection">Przeglądaj zbiory</Link>
              <Link className="retro-button retro-button-secondary" to={randomCollectionPath}>Odkryj eksponat</Link>
            </div>

            <div className="retro-stats" aria-label="Statystyki archiwum">
              <div className="retro-stat">
                <span className="retro-stat-value">{collectionTotal ?? '—'}</span>
                <span className="retro-stat-label">eksponatów</span>
              </div>
              <div className="retro-stat">
                <span className="retro-stat-value">{blogTotal ?? '—'}</span>
                <span className="retro-stat-label">wpisów w archiwum</span>
              </div>
            </div>
          </div>

          <div className="retro-hero-visual" aria-label="Logo StareKomputery.pl">
            <div className="retro-monitor">
              <div className="retro-monitor-screen">
                <div className="retro-logo-stage">
                  <img
                    className="retro-logo-image"
                    src={homepageLogoSrc}
                    alt="Stare Komputery"
                    width="500"
                    height="375"
                  />
                </div>
              </div>
              <div className="retro-terminal-lines">
                <span>&gt; LOAD "STAREKOMPUTERY.PL"</span>
                <span>Sprzęt · fotografie · wspomnienia</span>
              </div>
            </div>
          </div>
        </div>

        <div className="retro-content-grid">
          <section className="retro-panel retro-archive-panel" id="archiwum" aria-labelledby="archive-heading">
            <div className="retro-panel-header">
              <h2 id="archive-heading">Ostatnio w archiwum</h2>
              <span className="retro-panel-link">Relacje i notatki</span>
            </div>

            <div className="retro-archive-list">
              {blogError && <div className="retro-empty-state">Nie udało się pobrać aktualności.</div>}
              {blogsLoading && <div className="retro-empty-state" role="status">Ładowanie wpisów…</div>}
              {!blogError && !blogsLoading && data.length === 0 && <div className="retro-empty-state">Archiwum czeka na wpisy.</div>}
              {!blogError && data.map((d) => <HomepageBlog key={d.id} d={d} />)}
            </div>

            <Pagination page={page} totalPages={totalPages} pageHref={(value) => value === 1 ? '/' : `/?page=${value}`} ariaLabel="Stronicowanie aktualności" />
          </section>

          <section className="retro-panel retro-collections-panel" aria-labelledby="collections-heading">
            <div className="retro-panel-header">
              <h2 id="collections-heading">Nowe w zbiorach</h2>
              <Link to="/collection" className="retro-panel-link">Zobacz wszystkie</Link>
            </div>

            <div className="retro-collection-grid">
              {collectionError && <div className="retro-empty-state">Nie udało się pobrać najnowszych eksponatów.</div>}
              {collectionsLoading && <div className="retro-empty-state" role="status">Ładowanie eksponatów…</div>}
              {!collectionError && !collectionsLoading && dataNewPhoto.length === 0 && <div className="retro-empty-state">Zbiory czekają na nowe zdjęcia.</div>}
              {!collectionError && dataNewPhoto.map((d, index) => <HomepagePhoto key={d.id} d={d} eager={index < 2} />)}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}
